import React, { useState } from "react";
import { format } from "date-fns";
import { Calendar as CalendarIcon, Clock } from "lucide-react";
import { Calendar } from "./ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";

const HOURS = Array.from({ length: 24 }, (_, i) => String(i).padStart(2, "0"));
const MINUTES = ["00", "15", "30", "45"];

/**
 * value: Date | null
 * onChange: (Date | null) => void
 */
export default function DateTimePicker({ value, onChange, testId = "datetime-picker" }) {
  const [open, setOpen] = useState(false);
  const [hour, setHour] = useState(value ? String(value.getHours()).padStart(2, "0") : "10");
  const [minute, setMinute] = useState(value ? String(value.getMinutes()).padStart(2, "0") : "00");

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const handleDateSelect = (date) => {
    if (!date) return;
    const d = new Date(date);
    d.setHours(parseInt(hour, 10), parseInt(minute, 10), 0, 0);
    onChange(d);
  };

  const updateTime = (newHour, newMinute) => {
    setHour(newHour);
    setMinute(newMinute);
    if (value) {
      const d = new Date(value);
      d.setHours(parseInt(newHour, 10), parseInt(newMinute, 10), 0, 0);
      onChange(d);
    }
  };

  const display = value ? format(value, "EEE, dd MMM yyyy · HH:mm") : "Select date & time";

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid={`${testId}-trigger`}
          className={`flex items-center justify-between w-full bg-transparent border-b border-[#262626] focus:border-[#FFC300] py-3 outline-none transition-colors ${
            value ? "text-white" : "text-neutral-500"
          } hover:border-[#FFC300]`}
        >
          <span className="flex items-center gap-2">
            <CalendarIcon className="w-4 h-4 text-[#FFC300]" />
            {display}
          </span>
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="w-auto p-0 bg-[#0A0A0A] border border-[#FFC300] text-white rounded-none"
        data-testid={`${testId}-popover`}
      >
        <div className="p-2">
          <Calendar
            mode="single"
            selected={value}
            onSelect={handleDateSelect}
            disabled={(date) => date < today}
            initialFocus
            classNames={{
              caption_label: "text-sm font-bold uppercase tracking-widest text-white",
              nav_button:
                "h-7 w-7 bg-transparent p-0 text-white hover:bg-[#FFC300] hover:text-black border border-white/15 rounded-none",
              head_cell: "text-neutral-500 w-9 font-bold text-[10px] uppercase tracking-widest",
              day: "h-9 w-9 p-0 font-medium text-white hover:bg-white/10 rounded-none",
              day_selected:
                "bg-[#FFC300] text-black hover:bg-[#FFC300] hover:text-black focus:bg-[#FFC300] focus:text-black",
              day_today: "border border-[#FFC300] text-[#FFC300]",
              day_disabled: "text-neutral-700 opacity-40 cursor-not-allowed hover:bg-transparent",
              day_outside: "text-neutral-700 opacity-50",
            }}
          />
        </div>

        <div className="border-t border-white/10 p-4">
          <div className="flex items-center gap-2 mb-3">
            <Clock className="w-3.5 h-3.5 text-[#FFC300]" />
            <span className="text-[10px] uppercase tracking-[0.25em] font-bold text-neutral-400">
              Pickup Time
            </span>
          </div>
          <div className="flex items-center gap-2">
            <select
              data-testid={`${testId}-hour`}
              value={hour}
              onChange={(e) => updateTime(e.target.value, minute)}
              className="flex-1 bg-black border border-white/15 text-white px-3 py-2 text-sm font-bold focus:border-[#FFC300] outline-none"
            >
              {HOURS.map((h) => (
                <option key={h} value={h} className="bg-black">
                  {h}
                </option>
              ))}
            </select>
            <span className="text-white font-bold">:</span>
            <select
              data-testid={`${testId}-minute`}
              value={minute}
              onChange={(e) => updateTime(hour, e.target.value)}
              className="flex-1 bg-black border border-white/15 text-white px-3 py-2 text-sm font-bold focus:border-[#FFC300] outline-none"
            >
              {MINUTES.map((m) => (
                <option key={m} value={m} className="bg-black">
                  {m}
                </option>
              ))}
            </select>
          </div>

          <div className="flex gap-2 mt-4">
            <button
              type="button"
              onClick={() => {
                if (!value) {
                  const now = new Date();
                  now.setHours(parseInt(hour, 10), parseInt(minute, 10), 0, 0);
                  onChange(now);
                }
                setOpen(false);
              }}
              data-testid={`${testId}-confirm`}
              className="flex-1 bg-[#FFC300] text-black font-bold uppercase tracking-widest text-xs px-4 py-2 hover:bg-white transition-colors"
            >
              Confirm
            </button>
            <button
              type="button"
              onClick={() => {
                onChange(null);
                setOpen(false);
              }}
              className="border border-white/15 text-white font-bold uppercase tracking-widest text-xs px-4 py-2 hover:border-[#FFC300] hover:text-[#FFC300] transition-colors"
            >
              Clear
            </button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
